import React, {
    Children,
    isValidElement,
    cloneElement,
    ReactElement,
    forwardRef,
    Ref,
    useMemo,
    useEffect,
    useRef,
} from 'react'
import { DataTable, DataTableProps, DataTableValueArray } from 'primereact/datatable'
import { Skeleton } from 'primereact/skeleton'
import { ColumnProps } from 'primereact/column'

import useText from '../../hooks/useText'

export const INITIAL_DEFAULT_TABLE_ROWS = 15
export const INITIAL_DEFAULT_SIMPLIFIED_TABLE_ROWS = 5
export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250]

export const Table = forwardRef(
    <TValue extends DataTableValueArray>(
        {
            children,
            loading,
            fetching,
            onLoadMoreData,
            hasMoreData,
            simplified,
            ...props
        }: DataTableProps<TValue> & {
            children: ReactElement<ColumnProps>[]
            fetching?: boolean
            simplified?: boolean
            onLoadMoreData?: () => void
            hasMoreData?: boolean
        },
        ref: Ref<DataTable<TValue>>
    ) => {
        const text = useText()
        const observerTarget = useRef<Skeleton>(null)

        const explicitDataKey = props.dataKey as string | undefined
        const dataKeyValue = explicitDataKey || 'id'

        const value = useMemo(() => {
            if (loading) {
                return Array.from({ length: props.rows || 10 }).map((_, index) => ({
                    [dataKeyValue]: index,
                }))
            }
            if (onLoadMoreData && hasMoreData && !loading && !fetching) {
                return [
                    ...(props.value as TValue),
                    {
                        [dataKeyValue]: '__trigger',
                    },
                ]
            }
            if (fetching) {
                return [
                    ...(props.value as TValue),
                    ...Array.from({ length: 5 }).map((_, i) => ({
                        [dataKeyValue]: `__loading_${i}`,
                    })),
                ]
            }

            return props.value
        }, [dataKeyValue, fetching, hasMoreData, loading, onLoadMoreData, props.rows, props.value]) as TValue

        // Infinite scroll observer for dynamic row heights
        useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && typeof onLoadMoreData === 'function') {
                        onLoadMoreData()
                    }
                },
                { threshold: 1 }
            )

            const currentTarget = observerTarget.current?.getElement()
            if (currentTarget) {
                observer.observe(currentTarget)
            }

            return () => {
                if (currentTarget) {
                    observer.unobserve(currentTarget)
                }
            }
        }, [onLoadMoreData])

        const renderChildren = () => {
            if (loading) {
                return Children.map(children, (child, index) => {
                    if (isValidElement(child)) {
                        return cloneElement(child, { key: child.key ?? `column-${index}`, body: () => <Skeleton /> })
                    }
                    return child
                })
            }
            if (fetching) {
                return Children.map(children, (child, index) => {
                    if (isValidElement(child)) {
                        return cloneElement(child, {
                            key: child.key ?? `column-${index}`,
                            body: (item, columnOptions) => {
                                const idValue = item[dataKeyValue]

                                if (typeof idValue === 'string' && idValue.startsWith('__loading')) {
                                    return <Skeleton />
                                }
                                if (typeof child.props.body === 'function') {
                                    return child.props.body(item, columnOptions)
                                }

                                return child.props.body
                            },
                        })
                    }
                })
            }
            if (onLoadMoreData && hasMoreData && !fetching) {
                return Children.map(children, (child, index) => {
                    if (isValidElement(child)) {
                        return cloneElement(child, {
                            key: child.key ?? `column-${index}`,
                            body: (item, columnOptions) => {
                                const idValue = item[dataKeyValue]

                                if (idValue === '__trigger') {
                                    return <Skeleton ref={index === 0 ? observerTarget : undefined} />
                                }

                                if (typeof child.props.body === 'function') {
                                    return child.props.body(item, columnOptions)
                                }
                                return child.props.body
                            },
                        })
                    }
                })
            }

            return children
        }

        let firstRowsPerPageOption = INITIAL_DEFAULT_TABLE_ROWS
        if (simplified) {
            firstRowsPerPageOption = INITIAL_DEFAULT_SIMPLIFIED_TABLE_ROWS
        } else if (props?.rows && props?.rows < 25) {
            firstRowsPerPageOption = props.rows
        }

        return (
            <DataTable
                ref={ref}
                tableStyle={{ minWidth: simplified ? '25rem' : '50rem' }}
                dataKey={dataKeyValue}
                paginatorTemplate={`${simplified ? '' : 'RowsPerPageDropdown '}FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport`}
                currentPageReportTemplate={text('lblTableTotalResults')}
                emptyMessage={props.globalFilter == '' ? text('msgNoData') : text('msgNoResultsFound')}
                rowsPerPageOptions={simplified ? undefined : [firstRowsPerPageOption, ...DEFAULT_ROWS_PER_PAGE_OPTIONS]}
                rows={simplified ? INITIAL_DEFAULT_SIMPLIFIED_TABLE_ROWS : INITIAL_DEFAULT_TABLE_ROWS}
                {...props}
                paginator={(props.value?.length ?? 0) > 0 ? props.paginator : false}
                expandableRowGroups={loading ? undefined : props.expandableRowGroups}
                filters={loading ? undefined : props.filters}
                value={value}
            >
                {renderChildren()}
            </DataTable>
        )
    }
)

Table.displayName = 'Table'
